import React from "react";
import { useMarketingCopy } from "./MarketingShell";
import showcaseCopy from "./showcaseCopy.json";
import "./ShowcaseStories.css";

export type SceneKind = "connect" | "memory" | "permissions" | "risk" | "execute" | "security" | "evaluate" | "flywheel" | "become";
function DocumentMark() {
  return <g fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M-15-20H5l10 10v30h-30zM5-20v10h10M-7 0H7M-7 7H7M-7 14H2" /></g>;
}
function TaskMark() {
  return <g fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M-7-18h-10v39h34v-39H7" /><rect x="-7" y="-22" width="14" height="8" rx="2" />
    <path d="m-10-3 3 3 5-6M3-3h7m-20 13 3 3 5-6M3 10h7" />
  </g>;
}
function MicMark() {
  return <g fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><rect x="-7" y="-18" width="14" height="25" rx="7" /><path d="M-13-3v4a13 13 0 0026 0v-4M0 14v9M-7 23H7" /></g>;
}
function MailMark() {
  return <g fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round"><rect x="-20" y="-14" width="40" height="28" rx="3" /><path d="m-19-12 19 15 19-15" /></g>;
}
function ChartMark() {
  return <g fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M-18 15V-5M-6 15V-13M6 15V-3M18 15V-22" /></g>;
}
function CheckMark() {
  return <path d="m-8 0 5 5 12-12" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />;
}
function LockMark() {
  return <g fill="var(--scene-paper)" stroke="currentColor" strokeWidth="1.8"><rect x="-11" y="-2" width="22" height="19" rx="3" /><path d="M-7-2v-7a7 7 0 0114 0v7" fill="none" /><circle cy="6" r="2" fill="currentColor" /></g>;
}
function TeamMark() {
  return <g stroke="currentColor" strokeWidth="1.8" fill="none" strokeLinecap="round"><circle cy="-8" r="6" /><path d="M-12 14v-3a12 12 0 0124 0v3M-18-11a5 5 0 000 10M-22 11v-2a9 9 0 015-8M18-11a5 5 0 010 10M22 11v-2a9 9 0 00-5-8" /></g>;
}
function DatabaseMark() {
  return <g stroke="currentColor" strokeWidth="1.7" fill="var(--scene-paper)"><path d="M-39-30v60c0 22 78 22 78 0v-60" /><ellipse cy="-30" rx="39" ry="13" fill="var(--scene-tint)" /><path d="M-39-10c0 22 78 22 78 0M-39 10c0 22 78 22 78 0" fill="none" /></g>;
}
function FolderMark() {
  return <g fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round"><path d="M-19-12v-6h16l6 6h16v29h-38zM-19-7h38M-10 1h20M-10 7h13" /></g>;
}
function MemoryMark() {
  return <g stroke="currentColor" strokeWidth="1.6"><path d="M0 0-15-13M0 0l17-11M0 0l-13 17M0 0l16 14M-15-13l32 2M17-11l-1 25" fill="none" />{[[0,0,6],[-15,-13,4],[17,-11,4],[-13,17,4],[16,14,4]].map(([x,y,r]) => <circle key={`${x}-${y}`} cx={x} cy={y} r={r} fill="var(--scene-tint)" />)}</g>;
}
function ShieldMark() {
  return <g stroke="currentColor" strokeWidth="2" strokeLinejoin="round" fill="var(--scene-paper)"><path d="M0-25 23-15v20c0 15-23 27-23 27S-23 20-23 5v-20Z" /><CheckMark /></g>;
}
function ERPMark() {
  return <g fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round"><path d="M-17-15h34v32h-34zM-17-5h34M-5-5v22M6-5v22M-17 6h34M-10-21v6M10-21v6" /></g>;
}
function MeetingMark() {
  return <g fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><ellipse cy="5" rx="18" ry="8" /><circle cy="-15" r="5" /><circle cx="-22" cy="-5" r="4" /><circle cx="22" cy="-5" r="4" /><path d="M-6-7h12M-18 13v7M18 13v7" /></g>;
}
function EnterpriseMark({ index }: { index: number }) {
  return [<g key="database" transform="scale(.5)"><DatabaseMark /></g>, <MailMark key="mail" />, <TeamMark key="crm" />, <ERPMark key="erp" />, <MeetingMark key="meeting" />][index];
}

const modelAssets = ["openai.png", "anthropic.svg", "grok.png"];
type ScenePhase = "ready" | "playing" | "done";

function LedgerMark() {
  return <g fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M-17-20H8v39h-25ZM-10-11H1M-10-4H1M-10 3h6" /><ellipse cx="12" cy="5" rx="10" ry="4" /><path d="M2 5v12c0 6 20 6 20 0V5M2 11c0 6 20 6 20 0" /></g>;
}
function CodeMark() {
  return <g fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="m-10-9-10 9 10 9M10-9l10 9-10 9M4-16-4 16" /></g>;
}
function CampaignMark() {
  return <g fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round"><path d="M-18-5h10l24-12v34L-8 5h-10ZM-9 5l4 15h8L0 9M22-7l6-3M22 7l6 3" /></g>;
}

// Connection is intentionally simple; record-level relationships belong to Memory.
function ConnectedSources() {
  const sources = [[102,94], [250,62], [405,108], [385,274], [122,278]];
  const links = sources.map(([x,y]) => `M${x} ${y} 260 184`).join(" ");
  return <g className="connected-sources">
    <path className="scene-guide" d={links} />
    <path className="scene-trace" pathLength="1" d={links} />
    {sources.map(([x,y], index) => <g key={index} transform={`translate(${x} ${y})`}>
      <circle r="31" className="diagram-surface" />
      <g transform="scale(.85)"><EnterpriseMark index={index} /></g>
      <circle r="3" className="source-signal" style={{ "--signal-x": `${260-x}px`, "--signal-y": `${184-y}px`, "--signal-delay": `${index * .08}s` } as React.CSSProperties} />
    </g>)}
    <g transform="translate(260 184)"><g className="scene-receive">
      <circle r="39" className="diagram-surface diagram-tint" />
      <path d="m0-16 15 8v17L0 17-15 9V-8ZM-15-8 0 1l15-9M0 1v16" fill="none" stroke="currentColor" strokeWidth="1.5" />
    </g></g>
  </g>;
}

type MemoryPoint = { x: number; y: number; z: number; mark?: number };
const memoryPoints: MemoryPoint[] = [
  { x: -116, y: -58, z: 85, mark: 0 }, { x: 120, y: -68, z: -35, mark: 1 },
  { x: 93, y: 88, z: 90, mark: 2 }, { x: -108, y: 88, z: -90, mark: 3 },
  { x: 13, y: -109, z: -105, mark: 4 }, { x: 0, y: 0, z: 90 },
  ...[[-194,-110,-80],[-204,14,-10],[-139,134,20],[159,133,-35],[193,-111,35],[215,8,-40],
    [-154,-12,30],[-69,-99,-35],[-58,35,-40],[79,-126,-65],[165,-30,-70],[94,6,20],
    [165,67,-15],[30,126,-20],[-63,134,-100],[-162,61,-80],[-20,-55,-120],[42,40,-80],
    [-100,-3,-160],[71,-43,-160],[7,93,-155],[-30,-151,-95],[218,90,-105],[-207,85,-105],
    [-57,72,80],[147,17,80],[32,-64,110],[0,153,-30]].map(([x,y,z]) => ({ x, y, z })),
];
const memoryEdges = [[0,6],[0,7],[0,12],[0,13],[0,5],[0,26],[1,9],[1,10],[1,15],[1,16],[1,17],[1,28],
  [2,9],[2,17],[2,18],[2,19],[2,27],[2,5],[3,8],[3,14],[3,20],[3,21],[3,24],[3,26],
  [4,13],[4,15],[4,22],[4,23],[4,25],[5,14],[5,17],[5,22],[5,23],[5,28],
  [6,13],[6,25],[7,12],[7,29],[8,20],[8,31],[9,30],[10,11],[11,16],[11,27],[12,14],
  [14,22],[16,23],[17,23],[18,30],[19,24],[19,31],[20,24],[21,29],[22,23],[26,31],[27,30],
  [0,32],[4,32],[5,32],[19,33],[8,33],[2,33]];

function projectMemoryPoint(point: MemoryPoint, angle: number) {
  const x = point.x * Math.cos(angle) + point.z * Math.sin(angle);
  const z = point.z * Math.cos(angle) - point.x * Math.sin(angle);
  const scale = 560 / (560 - z);
  return { x: 260 + x * scale, y: 181 + point.y * scale, z, scale, mark: point.mark };
}

function SpatialMemoryGraph({ phase }: { phase: ScenePhase }) {
  const [progress, setProgress] = React.useState(0);
  React.useEffect(() => {
    if (phase !== "playing") return;
    // A small camera turn reveals true parallax. No ongoing render loop after 2s.
    let frame: number;
    let start: number | undefined;
    const turn = (time: number) => {
      if (start === undefined) start = time;
      const elapsed = Math.min((time - start) / 2000, 1);
      setProgress(1 - Math.pow(1 - elapsed, 3));
      if (elapsed < 1) frame = requestAnimationFrame(turn);
    };
    frame = requestAnimationFrame(turn);
    return () => cancelAnimationFrame(frame);
  }, [phase]);
  const angle = -.27 + .39 * (phase === "done" ? 1 : progress);
  const points = memoryPoints.map(point => projectMemoryPoint(point, angle));
  const marks = [<MicMark key="recordings" />, <MailMark key="email" />, <ChartMark key="revenue" />, <DocumentMark key="documents" />, <LedgerMark key="finance" />];
  return <g className="spatial-memory">
    {memoryEdges.map(([a,b], index) => {
      const p = points[a], q = points[b];
      const depth = Math.max(0, Math.min(1, ((p.z + q.z) / 2 + 160) / 270));
      return <path key={index} d={`M${p.x} ${p.y} ${q.x} ${q.y}`} fill="none" stroke="currentColor" strokeWidth={.7 + depth * .7} opacity={.1 + depth * .33} />;
    })}
    {points.map((point,index) => ({ ...point, index })).sort((a,b) => a.z - b.z).map(point => {
      const depth = Math.max(0, Math.min(1, (point.z + 160) / 270));
      return <g key={point.index} transform={`translate(${point.x} ${point.y}) scale(${point.scale})`} opacity={.34 + depth * .66}>
        {point.mark !== undefined ? <>
          <circle r="34" fill="var(--scene-accent)" opacity=".07" />
          <circle r="27" className="diagram-surface" />
          <g transform="scale(.8)">{marks[point.mark]}</g>
        </> : <>
          {point.index === 5 && <circle r="18" fill="var(--scene-accent)" opacity=".14" />}
          <circle r={point.index === 5 ? 8 : (point.index % 4 === 0 ? 5 : 3.4)} className="diagram-surface diagram-tint" />
        </>}
      </g>;
    })}
  </g>;
}

function TeamPermissionsTree() {
  const { language } = useMarketingCopy();
  const labels = (showcaseCopy[language] ?? showcaseCopy.en).departments;
  const datasets = (showcaseCopy[language] ?? showcaseCopy.en).departmentData;
  const columns = [65,195,325,455];
  const branches = "M260 78V108H65V137M260 108H455V137M195 108V137M325 108V137";
  return <g className="permissions-tree">
    <path className="scene-guide" d={branches} />
    <path className="scene-trace" pathLength="1" d={branches} />
    <g transform="translate(260 48)"><circle r="30" className="diagram-surface diagram-tint" /><g transform="scale(.7)"><ShieldMark /></g></g>
    {columns.map((x,index) => <g key={x} transform={`translate(${x} 0)`}>
      <g transform="translate(0 158)"><circle r="26" className="diagram-surface" /><g transform="scale(.76)"><TeamMark /></g></g>
      <text className="diagram-label" x="0" y="204">{labels[index]}</text>
      <path className="scene-guide" d="M0 216V250" />
      <path className="permission-branch" d="M0 216V250" />
      <g transform="translate(0 235)"><circle r="10" fill="var(--scene-paper)" /><g className="permission-granted"><g transform="scale(.6)"><CheckMark /></g></g></g>
      <g transform="translate(0 280)">
        <g transform="scale(.68)"><DatabaseMark /></g>
        <g transform="translate(0 -1) scale(.43)">{[<TeamMark key="sales" />,<CodeMark key="engineering" />,<ERPMark key="ops" />,<CampaignMark key="marketing" />][index]}</g>
        <g transform="translate(24 20) scale(.5)"><LockMark /></g>
      </g>
      <text className="diagram-label diagram-data-label" x="0" y="334">{datasets[index]}</text>
    </g>)}
  </g>;
}

function SecurityCommandLoop() {
  const loop = "M91 179V68Q91 47 112 47H408Q429 47 429 68V290Q429 311 408 311H112Q91 311 91 290Z";
  return <g className="security-command">
    <path className="command-loop" d={loop} />
    <path className="command-loop-progress" pathLength="1" d={loop} />
    <path className="command-arrows" d="m258 42 7 5-7 5m176 207-5 7-5-7m-162 57-7-5 7-5m-176-194 5-7 5 7" />
    <path className="scene-guide" d="M91 179h43M386 179h43M260 262v49" />
    <g className="command-dashboard">
      <rect x="134" y="91" width="252" height="171" rx="7" className="diagram-surface" />
      <path d="M134 116h252M272 128v119" className="scene-guide" />
      <circle cx="148" cy="104" r="2.5" fill="currentColor" opacity=".65" />
      <path d="M158 104h50" stroke="currentColor" strokeWidth="2" opacity=".45" />
      {[350,360,370].map(x => <circle key={x} cx={x} cy="104" r="2" fill="currentColor" opacity=".3" />)}
      <g transform="translate(208 172)">
        <circle r="40" fill="var(--scene-tint)" />
        <circle r="46" className="command-radar" />
        <g className="command-sweep"><path d="M0-46A46 46 0 0146 0" fill="none" stroke="var(--scene-accent)" strokeWidth="2.5" /><circle cy="-46" r="3" fill="var(--scene-accent)" /></g>
        <g transform="scale(.9)"><ShieldMark /></g>
      </g>
      <path d="M165 230h85M165 237h55" stroke="currentColor" strokeWidth="2" opacity=".3" />
      {[144,181,218].map((y,index) => <g key={y}>
        <path d={`M286 ${y-4}h41M286 ${y+4}h${28-index*5}`} stroke="currentColor" strokeWidth="2" opacity=".35" />
        <g transform={`translate(356 ${y})`}><circle r="10" fill="var(--scene-tint)" /><g className={`command-check command-check-${index}`}><g transform="scale(.6)"><CheckMark /></g></g></g>
      </g>)}
    </g>
    <g transform="translate(91 179)"><circle r="25" className="diagram-surface" /><g transform="scale(.34)"><DatabaseMark /></g></g>
    <g transform="translate(429 179)"><circle r="25" className="diagram-surface" /><path d="m2-15-12 17H0l-2 13L12-2H2Z" fill="none" stroke="currentColor" strokeWidth="1.6" /></g>
    <g transform="translate(260 311)"><circle r="23" className="diagram-surface" /><g transform="scale(.62)"><DocumentMark /></g><g className="command-feedback" transform="translate(17 13)"><circle r="10" fill="var(--scene-tint)" /><g transform="scale(.65)"><CheckMark /></g></g></g>
  </g>;
}

function CloudMissions() {
  const branches = "M244 205h48V121h55M292 205h55M292 205v84h55";
  const inputs = "M130 73v27h150V73M205 73v93";
  return <g className="cloud-missions">
    <path className="scene-guide" d={inputs} />
    <path className="scene-trace" pathLength="1" d={inputs} />
    {[<g key="database" transform="scale(.39)"><DatabaseMark /></g>, <FolderMark key="files" />, <MemoryMark key="memory" />].map((mark,index) => <g key={index} className="mission-input" transform={`translate(${130 + index * 75} 47)`}>
      <circle r="26" className="diagram-surface" />{mark}
    </g>)}
    <path className="scene-guide" d="M96 130h31l45 51M96 205h70M96 280h31l45-51" />
    {[130,205,280].map((y,index) => <g key={y} transform={`translate(73 ${y})`}>
      <circle r="23" fill="#fcfbf7" />
      <image href={`/brand-models/${modelAssets[index]}`} x="-13" y="-13" width="26" height="26" />
    </g>)}
    <path className="scene-guide" d={branches} />
    <path className="scene-trace" pathLength="1" d={branches} />
    <g transform="translate(205 205)"><g className="scene-receive">
      <circle r="39" className="diagram-surface diagram-tint" />
      <image href="/logo/logo.png" x="-34" y="-34" width="68" height="68" />
    </g></g>
    {[121,205,289].map((y,index) => <g key={y} transform={`translate(395 ${y})`}>
      <path d="M-29 28H31a24 24 0 003-48 31 31 0 00-57-10A29 29 0 00-29 28Z" className="diagram-surface" />
      <g transform="translate(0 1) scale(.72)"><TaskMark /></g>
      <g transform="translate(42 23)"><g className={`mission-check mission-check-${index}`}>
        <circle r="12" className="diagram-surface diagram-tint" /><g transform="scale(.7)"><CheckMark /></g>
      </g></g>
    </g>)}
  </g>;
}

function SceneDrawing({ kind, phase }: { kind: SceneKind; phase: ScenePhase }) {
  if (kind === "connect") return <ConnectedSources />;
  if (kind === "memory") return <SpatialMemoryGraph phase={phase} />;
  if (kind === "permissions") return <TeamPermissionsTree />;
  if (kind === "risk") return <>
    <path className="scene-guide" d="M84 77v210h354M84 131h354M84 210h354" />
    <path d="M87 217 124 184 166 202 202 152 239 176 278 123" className="risk-history" />
    <path d="m278 123 37 12 39 53 41 26 35 49" className="risk-downside" />
    <path className="risk-limit scene-resolve" d="M285 211h148" />
    <path className="scene-trace risk-recovery" pathLength="1" d="m278 123 37 12 39 32 40-47 36-29" />
    <g transform="translate(355 212)"><g className="scene-resolve"><ShieldMark /></g></g>
    <circle cx="278" cy="123" r="5" fill="currentColor" />
  </>;
  if (kind === "execute") return <CloudMissions />;
  if (kind === "security") return <SecurityCommandLoop />;
  if (kind === "evaluate") return <>
    <g transform="translate(114 180)"><circle r="34" fill="var(--scene-tint)" /><g transform="scale(.85)"><TaskMark /></g></g>
    <path className="scene-guide" d="M149 180h66" />
    <rect x="215" y="73" width="174" height="215" rx="5" fill="var(--scene-paper)" stroke="currentColor" strokeWidth="1.4" />
    <path d="M237 98h72M237 107h40" stroke="currentColor" strokeWidth="2" opacity=".3" />
    {[141,181,221,261].map((y,i) => <g key={y}><path d={`M238 ${y-3}h68M238 ${y+5}h${38+i*5}`} stroke="currentColor" strokeWidth="2" opacity=".3" /><g transform={`translate(355 ${y})`}><circle r="12" fill="var(--scene-tint)" /><g className={`evaluation-check check-${i}`}><g transform="scale(.7)"><CheckMark /></g></g></g></g>)}
    <path className="evaluation-scan" d="M228 121h147" stroke="var(--scene-accent)" strokeWidth="1.5" />
  </>;
  if (kind === "flywheel") return <>
    <path className="scene-guide" d="M260 69a111 111 0 11-1 0" />
    <path className="scene-trace flywheel-path" pathLength="1" d="M260 69a111 111 0 11-1 0" />
    <path d="m231 55 28 14-18 25" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
    {[[260,69], [371,180], [260,291], [149,180]].map(([x,y],i) => <g transform={`translate(${x} ${y})`} key={i}><circle r="27" fill="var(--scene-paper)" stroke="currentColor" strokeWidth="1.2" /><g transform="scale(.75)">{[<ChartMark key="chart" />, <path key="action" d="m3-23-20 27H-1l-3 20L18-4H2Z" fill="none" stroke="currentColor" strokeWidth="1.8" />, <CheckMark key="check" />, <DocumentMark key="doc" />][i]}</g></g>)}
    <g transform="translate(260 180)"><g className="scene-receive"><circle r="43" fill="var(--scene-tint)" /><path d="m-25 17 16-14 14 5 22-29M15-21h12v12" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" /></g></g>
    <g className="flywheel-feedback"><circle cx="260" cy="105" r="4" fill="var(--scene-accent)" /></g>
  </>;
  return <>
    <g className="becoming-orbit"><ellipse cx="260" cy="180" rx="128" ry="109" fill="none" stroke="currentColor" strokeWidth="1" opacity=".2" /><ellipse cx="260" cy="180" rx="65" ry="109" fill="none" stroke="currentColor" strokeWidth="1.3" transform="rotate(35 260 180)" opacity=".55" /></g>
    <path d="M111 258c50-75 97 39 144-54s100-24 154-110" fill="none" stroke="currentColor" strokeWidth="1.7" opacity=".6" />
    <g className="becoming-dot"><circle cx="260" cy="180" r="10" fill="var(--scene-accent)" /><circle cx="260" cy="180" r="24" fill="none" stroke="currentColor" opacity=".15" /></g>
  </>;
}

export function MotionScene({ kind, label, className = "" }: { kind: SceneKind; label: string; className?: string }) {
  const root = React.useRef<HTMLDivElement>(null);
  const [phase, setPhase] = React.useState<ScenePhase>("ready");
  React.useEffect(() => {
    const element = root.current;
    if (!element) return;
    const media = window.matchMedia("(prefers-reduced-motion: reduce)");
    let wait: ReturnType<typeof setTimeout> | undefined;
    let finish: ReturnType<typeof setTimeout> | undefined;
    let started = false;
    const reduce = () => {
      if (media.matches) { started = true; clearTimeout(wait); clearTimeout(finish); setPhase("done"); }
    };
    reduce();
    media.addEventListener("change", reduce);
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !started && !wait && !media.matches) {
        // Allow a reading pause. Play once, then retain the completed illustration.
        wait = setTimeout(() => {
          started = true;
          observer.disconnect();
          setPhase("playing");
          finish = setTimeout(() => setPhase("done"), 2000);
        }, 900);
      } else if (!entry.isIntersecting && !started) {
        clearTimeout(wait);
        wait = undefined;
      }
    }, { threshold: 0.6 });
    observer.observe(element);
    return () => { observer.disconnect(); media.removeEventListener("change", reduce); clearTimeout(wait); clearTimeout(finish); };
  }, []);
  return <div ref={root} className={`motion-scene scene-${kind} ${className}`} data-phase={phase}>
    <svg viewBox="0 0 520 360" role="img" aria-label={label} className="scene-drawing"><SceneDrawing kind={kind} phase={phase} /></svg>
  </div>;
}

export function ConnectorStrip() {
  const { language } = useMarketingCopy();
  const copy = showcaseCopy[language] ?? showcaseCopy.en;
  return <div className="showcase-connectors" aria-label={copy.connectionsLabel}>
    {copy.dataCategories.map((name,index) => <span key={name}><svg width="23" height="23" viewBox="-26 -26 52 52" aria-hidden="true"><EnterpriseMark index={index} /></svg>{name}</span>)}
  </div>;
}

export default function CapabilityShowcases({ overview = false }: { overview?: boolean }) {
  const { language } = useMarketingCopy();
  const copy = showcaseCopy[language] ?? showcaseCopy.en;
  const benchmarkLabel = language === "zh" ? "记忆基准测试" : language === "ja" ? "記憶ベンチマーク" : "Memory benchmarks";
  const citationLabel = language === "zh" ? "引用基准测试" : language === "ja" ? "引用ベンチマーク" : "Citation benchmark";
  const citationMetric = language === "zh" ? "引用评分" : language === "ja" ? "引用スコア" : "citation score";
  return <section id={overview ? undefined : "capabilities"} className="quant-wrap ordered-showcases" aria-label={copy.title}>
    {copy.rows.map(row => <article className="showcase-row" id={`capability-${row.kind}`} key={row.kind} aria-labelledby={`capability-title-${row.kind}`}>
      <div className="showcase-copy">
        <h2 id={`capability-title-${row.kind}`}><a className="section-title-link" href={`/blog/${row.articleSlug}`} aria-describedby={`capability-detail-${row.kind}`}>{row.title}</a></h2>
        <p className="showcase-detail" id={`capability-detail-${row.kind}`}>{row.description}</p>
        {row.kind === "memory" && <div className="showcase-evidence" aria-label={benchmarkLabel}>
          <a href="/research/memory-benchmarks#results"><strong>94.6/100</strong> LoCoMo</a>
          <a href="/research/memory-benchmarks#results"><strong>97.2/100</strong> LongMemEval</a>
        </div>}
        {row.kind === "evaluate" && <div className="showcase-evidence" aria-label={citationLabel}>
          <a href="/research/citation-benchmarks#results"><strong>98.88%</strong> {citationMetric}</a>
        </div>}
      </div>
      <MotionScene kind={row.kind as SceneKind} label={row.visualDescription} />
    </article>)}
  </section>;
}
